import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, X, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

export default function TelegramSection() {
  const qc = useQueryClient();
  const [token, setToken] = useState("");
  const [open, setOpen] = useState(false);

  const { data: status } = useQuery({
    queryKey: ["tgStatus"],
    queryFn: api.telegramStatus,
    refetchInterval: 30_000,
  });

  const setupMutation = useMutation({
    mutationFn: () => api.telegramSetup(token.trim()),
    onSuccess: () => {
      toast.success(tr("Telegram bot connected"));
      setToken("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["tgStatus"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Setup failed")),
  });

  const disableMutation = useMutation({
    mutationFn: api.telegramDisable,
    onSuccess: () => {
      toast.success(tr("Telegram bot disconnected"));
      qc.invalidateQueries({ queryKey: ["tgStatus"] });
    },
  });

  const enabled = !!status?.enabled;

  return (
    <>
      <div className="flex items-start gap-3">
        <Send className="w-4 h-4 text-[var(--accent)] mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-serif-display text-base">
            {enabled ? (
              <span className="inline-flex items-center gap-2">
                Connected · @{status?.bot_username}
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </span>
            ) : (
              tr("Not connected")
            )}
          </div>
          <p className="text-xs text-[var(--ink-muted)] mt-1 leading-relaxed">{trRich("Create a bot with <0>@BotFather</0>, copy the token, and paste it here. We’ll register a webhook — every message you forward to the bot will land in your<1>Telegram tips</1>queue.", null, [<a
              href="https://t.me/BotFather"
              target="_blank"
              rel="noreferrer"
              className="text-[var(--accent)] hover:underline" />, <a href="/telegram" className="text-[var(--accent)] hover:underline mx-1" />])}</p>
          {enabled && status?.webhook_info && (
            <div className="text-[11px] text-[var(--ink-muted)] mt-2 font-mono-display space-y-0.5">
              <div>{tr("Webhook: {webhook_url}", { webhook_url: status.webhook_url })}</div>
              <div>{tr("Pending updates: {pending_update_count}", { pending_update_count: status.webhook_info.pending_update_count ?? 0 })}
              </div>
              {status.webhook_info.last_error_message && (
                <div className="text-red-600">{tr("Last error: {last_error_message}", { last_error_message: status.webhook_info.last_error_message })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {!enabled ? (
          <>
            {!open ? (
              <button
                data-testid="tg-connect-toggle"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black"
              >{tr("Connect bot")}</button>
            ) : (
              <div className="w-full space-y-2">
                <label className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-display block">{tr("Bot token")}</label>
                <input
                  data-testid="tg-token-input"
                  type="password"
                  autoComplete="new-password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder={tr("123456789:ABCdefGhIjKlMnOpQrStUvWxYz-1234567890")}
                  className="w-full px-3 py-2 text-sm bg-white border border-[var(--border)] outline-none focus:border-[var(--ink)] font-mono-display"
                />
                <div className="flex gap-2">
                  <button
                    data-testid="tg-connect-submit"
                    onClick={() => setupMutation.mutate()}
                    disabled={setupMutation.isPending || token.length < 20}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-60"
                  >
                    {setupMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : null}{tr("Save & register webhook")}</button>
                  <button
                    onClick={() => {
                      setOpen(false);
                      setToken("");
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
                  >{tr("Cancel")}</button>
                </div>
                <p className="text-[11px] text-[var(--ink-muted)] font-mono-display">{tr("Token is stored server-side only. Never exposed to the browser after save.")}</p>
              </div>
            )}
          </>
        ) : (
          <>
            <a
              href={`https://t.me/${status?.bot_username}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
            >
              <ExternalLink className="w-3.5 h-3.5" />{tr("Open bot in Telegram")}</a>
            <button
              data-testid="tg-disconnect"
              onClick={() => {
                if (window.confirm(tr("Disconnect the Telegram bot?"))) {
                  disableMutation.mutate();
                }
              }}
              disabled={disableMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
            >
              <X className="w-3.5 h-3.5" />{tr("Disconnect")}</button>
          </>
        )}
      </div>
    </>
  );
}
